import React, { useState } from 'react';

import {
  ChevronRight,
  Folder,
  FolderOpen,
  Printer,
  Bell,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import ConfirmDeleteDialog from '@/components/ui/ConfirmDeleteDialog';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { apiService } from '../../../utils/api';
import PrintSettingsModal from '../modals/PrintSettingsModal';
import PartModal from '../modals/PartModal';
import { cn } from '@/utils/cn';
import { navTestId } from '@/utils/testId';
import { Badge } from '@/components/ui/badge';
import { Tooltip } from '@/components/ui/Tooltip';

export const TreeItemButton = ({
  onClick,
  text,
  disabled,
}: {
  onClick?: any;
  text: any;
  disabled?: any;
}) => {
  return (
    <button
      disabled={disabled}
      className={cn(
        'h-7 px-2 rounded text-xs text-neutral-700 hover:bg-neutral-150 hover:text-neutral-900 inline-flex items-center gap-1 transition-colors',
        disabled && 'bg-neutral-100 pointer-events-none cursor-not-allowed opacity-50'
      )}
      data-testid={`doc-tree-btn-${navTestId(text)}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
    >
      {text}
    </button>
  );
};

const TypeIcon = ({ type, open }: { type: string; open?: boolean }) => {
  const isFolder = type === 'PA' || type === 'SE';
  const isSheet = type === 'SH' || type === 'DR';
  const isAlert = type === 'PR';

  return (
    <span
      className={cn(
        'h-7 w-7 rounded inline-flex items-center justify-center shrink-0 border',
        isFolder &&
          (open
            ? 'bg-primary-50 border-primary-200 text-primary-700'
            : 'bg-neutral-50 border-neutral-200 text-neutral-600'),
        isSheet && 'bg-neutral-50 border-neutral-200 text-neutral-700',
        isAlert && 'bg-amber-50 border-amber-200 text-amber-700',
        !isFolder && !isSheet && !isAlert && 'bg-neutral-50 border-neutral-200 text-neutral-600'
      )}
    >
      {isFolder && (open ? <FolderOpen size={14} /> : <Folder size={14} />)}
      {isSheet && <Printer size={14} />}
      {isAlert && <Bell size={13} />}
      {!isFolder && !isSheet && !isAlert && <FileText size={14} />}
    </span>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  if (!status || status === 'N') return null;

  if (status === 'P' || status === 'G' || status === 'WP') {
    return <SpinnerOverlay />;
  }

  if (status === 'E' || status === 'MP') {
    return (
      <Badge variant="destructive" size="sm">
        <AlertTriangle size={10} /> Chyba
      </Badge>
    );
  }

  if (status === 'R') {
    return (
      <Badge variant="success" size="sm">
        Připraveno
      </Badge>
    );
  }

  return null;
};

const TreeItem = (props) => {
  const [open, setOpen] = useState(props.open);
  const [, setImmediate] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const { modelId, contract } = useSelector((state) => state.contract);
  const contractId = contract?.id ?? null;
  const { current_company } = useSelector((state) => state.auth);
  const queryClient = useQueryClient();
  const [partModalOpen, setPartModalOpen] = useState(false);

  const deleteDocument = useMutation({
    mutationFn: ({ id }: { id: any }) => {
      return apiService.delete(`/api/v1/document/${id}/remove`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['documentationTree', props.documentationId]);
    },
  });

  const updateDocPart = useMutation({
    mutationFn: ({ id, values }: { id: any; values: any }) => {
      return apiService.patch(`/api/v1/document/${id}/`, {
        ...values,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['documentationTree', props.documentationId]);
      setPartModalOpen(false);
    },
    onError: (err) => {
      console.error(err);
    },
  });

  const editFilter = useMutation({
    mutationFn: ({ id, values }: { id: any; values: any }) => {
      return apiService.patch(`/api/v1/filter/${id}/`, values);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(['filter', id]);
      setSettingsModalOpen(false);
    },
  });

  const createFilter = useMutation({
    mutationFn: ({ values }: { values: any }) => {
      return apiService.post(`/api/v1/filter/`, {
        ...values,
        model: modelId,
        contract: contractId,
        company: current_company,
        document: props.documentId,
      });
    },
    onSuccess: ({ data: filterData }) => {
      queryClient.setQueryData(['documentationTree', props.documentationId], () => {
        const newTree = props.updateFilter(filterData.id);
        return newTree;
      });
      setSettingsModalOpen(false);
    },
  });

  const handleEditDocument = (id, values) => {
    updateDocPart.mutate({ id, values });
  };

  const toggle = () => {
    if (!children) {
      return;
    }

    setOpen((open) => !open);
    setImmediate(false);
  };

  const { children, content, type, controls, tasks, status, filterId, item } = props;

  const handleSubmit = (filterId, values) => {
    if (filterId) {
      editFilter.mutate({ id: filterId, values });
    } else {
      createFilter.mutate({ values });
    }
  };

  const hasChildren = !!children;
  const isFolder = type === 'PA' || type === 'SE';
  const isSH = type === 'SH';

  return (
    <div className="w-full">
      <div
        className={cn(
          'group flex items-center gap-2 px-3 h-10 border-b border-neutral-100 transition-colors text-sm select-none cursor-pointer',
          'hover:bg-neutral-25'
        )}
        data-testid={`doc-tree-row-${props.documentId}`}
      >
        {/* Expander */}
        <button
          type="button"
          onClick={() => toggle()}
          className={cn(
            'h-5 w-5 inline-flex items-center justify-center rounded text-neutral-500 shrink-0',
            hasChildren ? 'hover:bg-neutral-150 hover:text-neutral-800' : 'opacity-0'
          )}
          data-testid={`doc-tree-toggle-${props.documentId}`}
        >
          <ChevronRight
            size={12}
            className={cn('transition-transform', open && 'rotate-90')}
            strokeWidth={2.5}
          />
        </button>

        {/* Type icon */}
        <TypeIcon type={type} open={open} />

        {/* Classification code badge */}
        {props.classification && (
          <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-500 px-1.5 py-0.5 rounded bg-neutral-100 shrink-0">
            {props.classification}
          </span>
        )}

        {/* Label */}
        <span
          className={cn(
            'text-sm truncate',
            isFolder ? 'font-semibold text-neutral-900' : 'text-neutral-800'
          )}
        >
          {content}
        </span>

        {/* PDF links */}
        <div className="flex items-center">
          {tasks?.map((task, i) => {
            if (task?.file) {
              return (
                <Tooltip key={i} content="Náhled PDF">
                  <a
                    href={task.file}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-0.5 text-[10px] text-neutral-500 bg-neutral-100 rounded px-1.5 h-5 ml-1 hover:bg-neutral-200 transition-colors"
                    data-testid={`doc-tree-pdf-link-${props.documentId}-${i}`}
                  >
                    <FileText size={10} />
                  </a>
                </Tooltip>
              );
            }
            return null;
          })}
        </div>

        {/* Status */}
        <StatusBadge status={status} />

        {/* Actions — identical structure to original */}
        <div className="ml-auto flex items-center gap-1 shrink-0">
          {type === 'SH' && (
            <TreeItemButton onClick={() => setSettingsModalOpen(true)} text="Nastavení tisku" />
          )}

          {controls}

          {!children && (
            <ConfirmDeleteDialog onConfirm={() => deleteDocument.mutate({ id: props.documentId })}>
              <TreeItemButton text="Odstranit" />
            </ConfirmDeleteDialog>
          )}

          <TreeItemButton onClick={() => setPartModalOpen(true)} text="Upravit" />
        </div>
      </div>

      {/* Children */}
      {open && children && <div className="pl-6">{children}</div>}

      {settingsModalOpen && (
        <PrintSettingsModal
          visible={settingsModalOpen}
          filterId={filterId}
          onSubmit={handleSubmit}
          document={props.item}
          onClose={() => {
            setSettingsModalOpen(false);
          }}
        />
      )}

      {partModalOpen && (
        <PartModal
          documentationId={props.documentationId}
          visible={partModalOpen}
          onSubmit={handleEditDocument}
          formProps={item}
          onClose={() => {
            setPartModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default TreeItem;
